import { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  useTimeOffTypes,
  useTimeOffBalances,
  useTimeOffRequests,
  useCreateTimeOffRequest,
  useCancelTimeOffRequest,
} from '@/hooks/use-api'
import { useToast } from '@/hooks/use-toast'
import { CalendarDays } from 'lucide-react'
import type { TimeOffRequest } from '@/types/hr'
import { explainError } from '@/lib/explain-error'

function statusVariant(s: TimeOffRequest['status']) {
  return s === 'Approved'
    ? 'default'
    : s === 'Pending'
      ? 'warning'
      : s === 'Rejected'
        ? 'destructive'
        : 'outline'
}

export default function TimeOffPage() {
  const { toast } = useToast()
  const { data: types } = useTimeOffTypes()
  const { data: balances } = useTimeOffBalances()
  const { data: requests } = useTimeOffRequests()
  const createReq = useCreateTimeOffRequest()
  const cancelReq = useCancelTimeOffRequest()

  const [typeId, setTypeId] = useState('')
  const [start, setStart] = useState('')
  const [end, setEnd] = useState('')
  const [amount, setAmount] = useState('')
  const [reason, setReason] = useState('')

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!typeId || !start || !end || !amount) {
      toast({ title: 'Please fill in type, dates and amount', variant: 'destructive' })
      return
    }
    try {
      await createReq.mutateAsync({
        type_id: typeId,
        start_date: start,
        end_date: end,
        amount: parseFloat(amount),
        unit: 'days',
        reason: reason || undefined,
      })
      toast({ title: 'Request submitted', description: 'Your manager has been notified.' })
      setTypeId('')
      setStart('')
      setEnd('')
      setAmount('')
      setReason('')
    } catch (err: any) {
      toast({
        title: 'Could not submit',
        description: explainError(err, 'Request failed.'),
        variant: 'destructive',
      })
    }
  }

  return (
    <main className="container mx-auto max-w-4xl p-6">
      <header className="mb-6">
        <h1 className="flex items-center gap-2 text-2xl font-bold">
          <CalendarDays className="h-6 w-6" aria-hidden="true" />
          Time Off
        </h1>
        <p className="text-sm text-muted-foreground">Your leave balances and requests</p>
      </header>

      {/* Balances */}
      <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
        {(balances ?? []).map((b) => (
          <Card key={b.type_id}>
            <CardContent className="p-4">
              <p className="text-xs text-muted-foreground">{b.type_name}</p>
              <p className="text-2xl font-bold">{b.remaining}</p>
              <p className="text-xs text-muted-foreground">
                of {b.allowance} {b.unit} ({b.used} used)
              </p>
            </CardContent>
          </Card>
        ))}
        {balances && balances.length === 0 && (
          <p className="text-sm text-muted-foreground">No leave entitlements set yet.</p>
        )}
      </div>

      {/* Request form */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-sm font-medium">Request time off</CardTitle>
          <CardDescription>Submit a request for approval by your manager.</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={submit} className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label htmlFor="to-type" className="mb-1 block text-sm">
                Type
              </label>
              <Select value={typeId} onValueChange={setTypeId}>
                <SelectTrigger id="to-type" aria-label="Leave type">
                  <SelectValue placeholder="Select type…" />
                </SelectTrigger>
                <SelectContent>
                  {(types ?? []).map((t) => (
                    <SelectItem key={t.id} value={t.id}>
                      {t.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label htmlFor="to-amount" className="mb-1 block text-sm">
                Days
              </label>
              <Input
                id="to-amount"
                type="number"
                min="0.5"
                step="0.5"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
              />
            </div>
            <div>
              <label htmlFor="to-start" className="mb-1 block text-sm">
                Start date
              </label>
              <Input
                id="to-start"
                type="date"
                value={start}
                onChange={(e) => setStart(e.target.value)}
              />
            </div>
            <div>
              <label htmlFor="to-end" className="mb-1 block text-sm">
                End date
              </label>
              <Input id="to-end" type="date" value={end} onChange={(e) => setEnd(e.target.value)} />
            </div>
            <div className="sm:col-span-2">
              <label htmlFor="to-reason" className="mb-1 block text-sm">
                Reason (optional)
              </label>
              <Input id="to-reason" value={reason} onChange={(e) => setReason(e.target.value)} />
            </div>
            <div className="sm:col-span-2">
              <Button type="submit" disabled={createReq.isPending}>
                {createReq.isPending ? 'Submitting…' : 'Submit request'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* My requests */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-medium">My requests</CardTitle>
        </CardHeader>
        <CardContent>
          {!requests || requests.length === 0 ? (
            <p className="text-sm text-muted-foreground">No requests yet.</p>
          ) : (
            <ul className="divide-y divide-border">
              {requests.map((r) => (
                <li key={r.id} className="flex items-center justify-between gap-3 py-2 text-sm">
                  <div>
                    <span className="font-medium">{r.type_name}</span> · {r.amount} {r.unit} ·{' '}
                    {r.start_date} → {r.end_date}
                    {r.decision_note ? (
                      <span className="block text-xs text-muted-foreground">{r.decision_note}</span>
                    ) : null}
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant={statusVariant(r.status)}>{r.status}</Badge>
                    {r.status === 'Pending' && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => cancelReq.mutate(r.id)}
                        aria-label={`Cancel request for ${r.type_name}`}
                      >
                        Cancel
                      </Button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </main>
  )
}
